import React, { useEffect, useState, useCallback } from "react";
import moment from "moment";
import {
  Button,
  Card,
  Typography,
  notification,
  Table,
  Modal,
  message,
  Collapse,
  Input,
  Flex,
  Popconfirm,
  Tooltip,
  Select,
  Switch,
  Tag,
  Alert,
} from "antd";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  EditFilled,
  DeleteFilled,
  InfoCircleOutlined,
  TagsOutlined,
} from "@ant-design/icons";
import axiosInstance from "../../redux/apiAxios";
import ProvidersTags from "../../components/ProvidersTags/ProvidersTags";
import Editor from "../../components/Editor";
import { fetchSTJobTags } from "../../redux/organizationSlice";

const { Title } = Typography;
const { Panel } = Collapse;
const { Option } = Select;

const stripHtmlTags = (html) => {
  if (!html) return "";
  const text = html.replace(/<[^>]*>/g, "");
  const tmp = document.createElement("DIV");
  tmp.innerHTML = text;
  return tmp.textContent || tmp.innerText || "";
};

const membershipSchema = yup.object().shape({
  title: yup.string().required("Title is required"),
  description: yup
    .string()
    .required("Description is required")
    .test("max-length", "Description must be less than 700 characters", (value) => {
      const plainText = stripHtmlTags(value || "");
      return plainText.length <= 700;
    }),
  buttonText: yup.string().max(50, "Button text must be less than 50 characters"),
  trade: yup.string().required("Trade is required"),
  serviceType: yup.array().of(yup.string()).required("At least one service type is required"),
  jobTags: yup.array().of(yup.string()),
  notificationAccounts: yup.array().of(yup.string()),
});

export default function Membership() {
  const { organizationData, serviceTitanJobTags, serviveTitanLoading } = useSelector((state) => state.organization);
  const { trades, services } = useSelector((state) => state.trade);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [memberships, setMemberships] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [updateModal, setUpdateModal] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [pageSize, setPageSize] = useState(5);
  const [messageApi, contextHolder] = message.useMessage();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [buttonText, setButtonText] = useState("");
  const [yesButtonText, setYesButtonText] = useState("Yes");
  const [noButtonText, setNoButtonText] = useState("No");
  const [selectedTrade, setSelectedTrade] = useState(null);
  const [selectedServiceTypes, setSelectedServiceTypes] = useState([]);
  const [jobTags, setJobTags] = useState([]);
  const [notificationAccounts, setNotificationAccounts] = useState([]);
  const [membershipOrder, setMembershipOrder] = useState("prebooking");
  const [editingMembershipId, setEditingMembershipId] = useState(null);

  useEffect(() => {
    if (organizationData?.organization_provider === "HCP") {
      navigate("/orgSettings", { replace: true });
      return;
    }
  }, [organizationData?.organization_provider, navigate]);

  const fetchMemberships = useCallback(async () => {
    if (!organizationData?._id) return;
    setTableLoading(true);
    try {
      const response = await axiosInstance.get(
        `/org/memberships/${organizationData._id}`
      );
      setMemberships(response?.data || []);
    } catch (error) {
      console.error("Error fetching memberships:", error);
      notification.error({
        message: "Error",
        description: "Failed to fetch memberships.",
        duration: 3,
      });
      setMemberships([]);
    } finally {
      setTableLoading(false);
    }
  }, [organizationData?._id]);

  useEffect(() => {
    if (organizationData?._id && organizationData?.organization_provider !== "HCP") {
      fetchMemberships();
      dispatch(fetchSTJobTags(organizationData._id));
    }
  }, [organizationData?._id, organizationData?.organization_provider, fetchMemberships]);

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setButtonText("");
    setYesButtonText("Yes");
    setNoButtonText("No");
    setSelectedTrade(null);
    setSelectedServiceTypes([]);
    setJobTags([]);
    setNotificationAccounts([]);
    setMembershipOrder("prebooking");
    setEditingMembershipId(null);
    setUpdateModal(false);
    setSubmitLoading(false);
  };

  const handleCreateMembership = () => {
    resetForm();
    setIsModalVisible(true);
  };

  const handleEditMembership = (record) => {
    setTitle(record.title || "");
    setDescription(record.description || "");
    setYesButtonText(record.yesButtonText || "Yes");
    setNoButtonText(record.noButtonText || "No");
    setSelectedTrade(record.trade?._id || record.trade || null);
    setSelectedServiceTypes(
      record.serviceType?.map((s) => s._id || s) || []
    );
    setJobTags(
      record.jobTags?.map((tag) => {
        if (typeof tag === 'string') return tag;
        if (typeof tag === 'object') {
          return tag._id || tag.id || tag.name || tag;
        }
        return tag;
      }) || []
    );
    setNotificationAccounts(record.notificationAccounts || []);
    setMembershipOrder(record.membershipOrder || "prebooking");
    setEditingMembershipId(record._id);
    setUpdateModal(true); 
    setIsModalVisible(true);
  };

  const handleDeleteMembership = async (membershipId) => {
    try {
      await axiosInstance.delete(
        `/org/memberships/${organizationData._id}/${membershipId}`
      );
      const updatedMemberships = memberships.filter(
        (m) => m._id !== membershipId
      );
      setMemberships(updatedMemberships);
      messageApi.success({
        type: "success",
        content: "Membership deleted successfully.",
      });
    } catch (error) {
      console.error("Error deleting membership:", error);
      notification.error({
        message: "Error",
        description: "Failed to delete membership.",
        duration: 3,
      });
    }
  };

  const handleStatusToggle = async (membershipId, newStatus) => {
    try {
      await axiosInstance.put(
        `/org/memberships/${organizationData._id}/${membershipId}`,
        { membershipStatus: newStatus }
      );
      const updatedMemberships = memberships.map((m) =>
        m._id === membershipId ? { ...m, membershipStatus: newStatus } : m
      );
      setMemberships(updatedMemberships);
      messageApi.success({
        type: "success",
        content: `Membership ${newStatus ? "activated" : "deactivated"} successfully.`,
      });
    } catch (error) {
      console.error("Error updating membership status:", error);
      notification.error({
        message: "Error",
        description: "Failed to update membership status.",
        duration: 3,
      });
    }
  };

  const handleSubmitMembership = async () => {
    const sanitizedJobTags = jobTags.map((tag) => {
      if (typeof tag === 'string') return tag;
      if (typeof tag === 'object') {
        return tag._id || tag.id || tag.name || String(tag);
      }
      return String(tag);
    });

    const payload = {
        organizationId: organizationData._id,
        title: title.trim(),
        description: description || "",
        buttonText: buttonText.trim(),
        yesButtonText: yesButtonText.trim(),
        noButtonText: noButtonText.trim(),
        trade: selectedTrade,
        serviceType: selectedServiceTypes,
        jobTags: sanitizedJobTags,
        membershipStatus: true,
        notificationAccounts: notificationAccounts,
        membershipOrder: membershipOrder,
      };
    try {
      await membershipSchema.validate(payload, { abortEarly: false });
    } catch (error) {
      console.error("Error validating membership:", error);
      error.errors.forEach((err) => notification.error({
        message: "Error",
        description: err,
        duration: 3,
      }));
      return
    }
    setSubmitLoading(true);
    try {
      if (updateModal && editingMembershipId) {
        await axiosInstance.put(
          `/org/memberships/${organizationData._id}/${editingMembershipId}`,
          payload
        );
        messageApi.success({
          type: "success",
          content: "Membership updated successfully.",
        });
      } else {
        await axiosInstance.post(`/org/memberships/${organizationData._id}`, payload);
        messageApi.success({
          type: "success",
          content: "Membership created successfully.",
        });
      }
      setIsModalVisible(false);
      resetForm();
      fetchMemberships();
    } catch (error) {
      console.error("Error saving membership:", error);
      notification.error({
        message: "Error",
        description:
          error.response?.data?.message ||
          "Failed to save membership. " +
            (error.response?.data?.errors?.trade?.message || ""),
        duration: 5,
      });
    } finally {
      setSubmitLoading(false);
    }
  };

  const filterOption = (input, option) => {
    return (
      (option?.children || option?.label || "")
        .toLowerCase()
        .includes(input.toLowerCase()) ||
      (option?.value || "").toLowerCase().includes(input.toLowerCase())
    );
  };

  const columns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      render: (text) => (
        <span style={{ fontWeight: "600", color: "#374151" }}>{text}</span>
      ),
    },
    {
      title: "Trade",
      dataIndex: "trade",
      key: "trade",
      render: (trade) => (
        <span style={{ color: "#6b7280" }}>
          {trade?.name || trade?.tradeName || "N/A"}
        </span>
      ),
    },
    {
      title: "Service Types",
      dataIndex: "serviceType",
      key: "serviceType",
      render: (serviceTypes) => {
        if (!serviceTypes || !Array.isArray(serviceTypes)) return null
        return (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
            {serviceTypes.slice(0, 2).map((service, idx) => {
              const serviceName = typeof service === 'string' 
                ? service 
                : (service?.name || service?.serviceName || service?._id || 'N/A');
              return (
                <Tag key={idx} color="blue">
                  {serviceName}
                </Tag>
              );
            })} 
            {serviceTypes.length > 2 && (
              <Tag color="default">+{serviceTypes.length - 2} more</Tag>
            )}
          </div>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "membershipStatus",
      key: "membershipStatus",
      render: (status, record) => (
        <Switch
          checked={status}
          onChange={(checked) => handleStatusToggle(record._id, checked)}
          checkedChildren="Active"
          unCheckedChildren="Inactive"
        />
      ),
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text) => (
        <span style={{ color: "#6b7280" }}>
          {text ? moment(text).format("MMM DD, YYYY") : "N/A"}
        </span>
      ),
    },
    {
      title: "Action",
      render: (_, record) => (
        <div style={{ display: "flex", gap: "16px" }}>
          <Tooltip title="Edit">
            <EditFilled
              style={{
                fontSize: 20,
                color: "#1890ff",
                cursor: "pointer",  
              }}
              onClick={() => handleEditMembership(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Delete Membership"
            description="Are you sure you want to delete this membership?"
            onConfirm={() => handleDeleteMembership(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <DeleteFilled
                style={{
                  fontSize: 20,
                  color: "#ff4d4f",
                  cursor: "pointer",
                }}
              />
            </Tooltip>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <>
      {contextHolder}
      <Flex align="center" gap={10} style={{ marginBottom: "20px" }}>
        <TagsOutlined style={{ fontSize: 20 }} />
        <Title level={3} style={{ margin: 0 }}>
          Memberships
        </Title>
      </Flex>

      <div
        style={{
          padding: "24px",
          maxWidth: "100%",
          margin: "0 auto",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: "16px",
          }}
        >
          <Button
            type="default"
            style={{
              background: "rgb(4 42 87)",
              color: "#fff",
              fontSize: "12px",
              padding: "4px 12px",
              borderRadius: "5px",
              border: "none",
            }}
            onClick={handleCreateMembership}
          >
            Create New Membership
          </Button>
        </div>

        <Card
          bordered={false}
          style={{
            width: "100%",
            boxShadow: "0 4px 16px rgba(0, 0, 0, 0.1)",
            borderRadius: "10px",
          }}
        >
          <Table
            dataSource={memberships}
            columns={columns}
            rowKey="_id"
            loading={tableLoading}
            pagination={{
              defaultPageSize: 5,
              pageSize: pageSize,
              showSizeChanger: true,
              showQuickJumper: true,
              pageSizeOptions: ["5", "10", "20", "50"],
            }}
            onChange={(pagination) => {
              setPageSize(pagination?.pageSize ?? 5);
            }}
            style={{ marginTop: "16px" }}
          />
        </Card>
      </div>

      <Modal
        title={
          updateModal ? "Update Membership" : "Create New Membership"
        }
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          resetForm();
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setIsModalVisible(false);
              resetForm();
            }}
            style={{
              fontSize: "10px",
              padding: "1px 8px",
              height: "24px",
              borderRadius: "5px",
              color: "#6b7280",
            }}
          >
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={handleSubmitMembership}
            loading={submitLoading}
            disabled={submitLoading}
            style={{
              backgroundColor: "rgb(4 42 87)",
              fontSize: "10px",
              padding: "1px 8px",
              height: "24px",
              borderRadius: "5px",
              border: "none",
            }}
          >
            {updateModal ? "Update Membership" : "Create Membership"}
          </Button>,
        ]}
        width={800}
      >
        <Collapse defaultActiveKey={["1", "2", "3", "4"]}>
          <Panel header="Basic Information" key="1">
            <div style={{ marginBottom: "16px" }}>
              <label
                style={{
                  fontWeight: "600",
                  color: "#374151",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  marginBottom: "8px",
                }}
              >
                Membership Order
                <Tooltip title="Pre-Booking: Membership signup happens before booking. Post-Booking: Membership signup happens after booking.">
                  <InfoCircleOutlined style={{ fontSize: "14px", color: "#1890ff", cursor: "help" }} />
                </Tooltip>
              </label>
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <Switch
                  checked={membershipOrder === "postbooking"}
                  onChange={(checked) => setMembershipOrder(checked ? "postbooking" : "prebooking")}
                  checkedChildren="Post-Booking"
                  unCheckedChildren="Pre-Booking"
                />
              </div>
            </div>
            <div style={{ marginBottom: "16px" }}>
              <label
                style={{
                  fontWeight: "600",
                  color: "#374151",
                  display: "block",
                  marginBottom: "8px",
                }}
              >
                Title <span style={{ color: "red" }}>*</span>
              </label>
              <Input
                placeholder="Enter membership title"
                onChange={(e) => setTitle(e.target.value)}
                value={title}
                maxLength={50}
                showCount
                style={{
                  width: "100%",
                  marginBottom: "16px",
                  borderRadius: "5px",
                }}
              />
            </div>
            <div style={{ marginBottom: "16px" }}>
              <label
                style={{
                  fontWeight: "600",
                  color: "#374151",
                  display: "block",
                  marginBottom: "8px",
                }}
              >
                Description <span style={{ color: "red" }}>*</span>
              </label>
              <div style={{ marginBottom: "8px" }}>
                <Editor
                  bullets={true}
                  key={editingMembershipId || 'new'}
                  value={description}
                  onChange={(value) => setDescription(value)}
                />
                <div style={{ marginTop: "8px", fontSize: "12px", color: "#6b7280" }}>
                  {stripHtmlTags(description).length} / 700 characters
                </div>
              </div>
            </div>
            <div style={{ marginBottom: "16px" }}>
              <label
                style={{
                  fontWeight: "600",
                  color: "#374151",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  marginBottom: "8px",
                }}
              >
                "Yes" Button Text
                <Tooltip title="This text will be displayed on the confirmation button when customers choose to sign up for the membership">
                  <InfoCircleOutlined style={{ fontSize: "14px", color: "#1890ff", cursor: "help" }} />
                </Tooltip>
              </label>
              <Input
                placeholder="Yes"
                onChange={(e) => setYesButtonText(e.target.value)}
                value={yesButtonText}
                maxLength={20}
                showCount
                style={{
                  width: "100%",
                  borderRadius: "5px",
                }}
              />
            </div>
            <div style={{ marginBottom: "16px" }}>
              <label
                style={{
                  fontWeight: "600",
                  color: "#374151",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  marginBottom: "8px",
                }}
              >
                "No" Button Text
                <Tooltip title="This text will be displayed on the button when customers choose to continue without signing up for the membership.">
                  <InfoCircleOutlined style={{ fontSize: "14px", color: "#1890ff", cursor: "help" }} />
                </Tooltip>
              </label>
              <Input
                placeholder="No"
                onChange={(e) => setNoButtonText(e.target.value)}
                value={noButtonText}
                maxLength={20}
                showCount
                style={{
                  width: "100%",
                  borderRadius: "5px",
                }}
              />
            </div>
          </Panel>
          <Panel header="Trade & Service Types" key="2">
            <div style={{ marginBottom: "16px" }}>
              <label
                style={{
                  fontWeight: "600",
                  color: "#374151",
                  display: "block",
                  marginBottom: "8px",
                }}
              >
                Trade <span style={{ color: "red" }}>*</span>
              </label>
              <Select
                showSearch
                filterOption={filterOption}
                placeholder="Select Trade"
                value={selectedTrade}
                onChange={setSelectedTrade}
                style={{ width: "100%" }}
              >
                {trades?.map((trade) => (
                  <Option key={trade._id} value={trade._id}>
                    {trade.tradeName}
                  </Option>
                ))}
              </Select>
            </div>
            <div style={{ marginBottom: "16px" }}>
              <label
                style={{
                  fontWeight: "600",
                  color: "#374151",
                  display: "block",
                  marginBottom: "8px",
                }}
              >
                Service Types <span style={{ color: "red" }}>*</span>
              </label>
              <Select
                mode="multiple"
                showSearch
                filterOption={filterOption}
                placeholder="Select Service Types"
                value={selectedServiceTypes}
                onChange={setSelectedServiceTypes}
                style={{ width: "100%" }}
              >
                {services?.map((service) => (
                  <Option key={service._id} value={service._id}>
                    {service.name}
                  </Option>
                ))}
              </Select>
            </div>
          </Panel>
          <Panel header="Booking Tags" key="3">
            <div style={{ marginBottom: "16px" }}>
              <label
                style={{
                  fontWeight: "600",
                  color: "#374151",
                  display: "block",
                  marginBottom: "8px",
                }}
              >
                Booking Tags
              </label>
              <ProvidersTags
                tagIds={jobTags}
                onTagChange={(value) => {
                  setJobTags([...value]);
                }}
                loading={serviveTitanLoading || !serviceTitanJobTags || !serviceTitanJobTags?.data}
                placeholder="Select booking tags"
                showSyncButton={false}
              />
              <Alert
                description="When a customer signs up for this membership, the selected tags will be automatically attached to the booked job."
                type="info"
                showIcon
                style={{
                  marginTop: "12px",
                  borderRadius: "6px",
                  padding: "8px 12px",
                  fontSize: "12px",
                }}
              />
            </div>
          </Panel>
          <Panel header="Notification Accounts" key="4">
            <div style={{ marginBottom: "16px" }}>
              <label
                style={{
                  fontWeight: "600",
                  color: "#374151",
                  display: "block",
                  marginBottom: "8px",
                }}
              >
                Notification Accounts
              </label>
              <Select
                mode="tags"
                style={{ width: "100%" }}
                placeholder="Add notification accounts (e.g., email@example.com)"
                value={notificationAccounts}
                onChange={setNotificationAccounts}
                tokenSeparators={[","]}
                open={false}
                suffixIcon={null}
              >
                {notificationAccounts.map((account) => (
                  <Option key={account} value={account}>
                    {account}
                  </Option>
                ))}
              </Select>
              <Alert
                description="The accounts mentioned here will receive notification emails when a customer signs up for this membership."
                type="info"
                showIcon
                style={{
                  marginTop: "12px",
                  borderRadius: "6px",
                  padding: "8px 12px",
                  fontSize: "12px",
                }}
              />
            </div>
          </Panel>
        </Collapse>
      </Modal>
    </>
  );
}

